import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime, timeAgo, ACTIVITY_LABELS } from "@/lib/format";
import { Empty } from "@/components/bits";
import { cn } from "@/lib/utils";

const TONE = {
  fill: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  source_error: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  sign_in_locked: "text-rose-300 border-rose-500/30 bg-rose-500/10",
  member_removed: "text-rose-300 border-rose-500/30 bg-rose-500/10",
  source_disconnected: "text-rose-300 border-rose-500/30 bg-rose-500/10",
};

function detailText(a) {
  const d = a.detail || {};
  switch (a.type) {
    case "fill": return `on ${d.host || "unknown site"}`;
    case "sign_in": return `via ${d.client || "hub"}`;
    case "source_connected": case "source_disconnected": case "source_updated": return [d.source, d.mailbox, d.numbers != null ? `${d.numbers} number(s)` : null].filter(Boolean).join(" · ");
    case "source_error": return `${d.source} · ${d.mailbox} — ${d.detail}`;
    case "member_added": return `${d.member}${d.is_admin ? " (admin)" : ""}`;
    case "member_removed": return d.member;
    case "member_role_changed": return `${d.member} → ${d.is_admin ? "admin" : "member"}`;
    case "test_code_sent": return `code ${d.code}`;
    default: return Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(" · ");
  }
}

export default function Activity() {
  const q = useQuery({ queryKey: ["activity"], queryFn: () => api.get("/activity", { params: { limit: 200 } }).then((r) => r.data), refetchInterval: 10000, staleTime: 0 });
  const items = q.data || [];
  return (
    <div className="rise flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-100">Activity</h1>
        <p className="mt-1 text-sm text-zinc-400">Sign-ins, fills, source and member changes. Kept for one year.</p>
      </div>
      {items.length === 0 ? (
        <Empty title="Nothing yet" testId="activity-empty">Activity appears here as your team signs in and fills codes.</Empty>
      ) : (
        <div className="surface overflow-x-auto">
          <table className="table-dense w-full" aria-label="Activity" data-testid="activity-table">
            <thead className="border-b border-[#242A36]"><tr><th>When</th><th>Event</th><th>Who</th><th>Details</th></tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`activity-row-${a.id}`}>
                  <td className="whitespace-nowrap font-mono text-xs text-zinc-500" title={fmtDateTime(a.created_at)}>{timeAgo(a.created_at)}</td>
                  <td><span className={cn("inline-flex rounded border px-1.5 py-0.5 text-[11px] font-semibold", TONE[a.type] || "border-zinc-700 bg-zinc-800/60 text-zinc-300")}>{ACTIVITY_LABELS[a.type] || a.type}</span></td>
                  <td className="text-zinc-300">{a.actor_email || <span className="text-zinc-600">system</span>}</td>
                  <td className="text-xs text-zinc-400">{detailText(a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
